import { useEffect, useRef, useState, useEffectEvent } from "react";
import { useSearch } from "@tanstack/react-router";

export function useSearchLoading() {
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const search = useSearch({ strict: false }) as Record<string, any>;
  const previousParamsRef = useRef<string>("");
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialMount = useRef(true);

  const handleLoadingComplete = useEffectEvent(() => {
    setIsSearchLoading(false);
  });

  const checkParamChanges = useEffectEvent((currentParams: string) => {
    const hasSearchChange =
      search.search !==
      new URLSearchParams(previousParamsRef.current).get("search");

    const hasFilterChange =
      currentParams !== previousParamsRef.current &&
      previousParamsRef.current !== "";

    return hasSearchChange || hasFilterChange;
  });

  useEffect(() => {
    // Convertir search params a string
    const params = new URLSearchParams();
    Object.entries(search).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.set(key, value.toString());
      }
    });
    const currentParams = params.toString();

    // En el montaje inicial, solo guardar los parámetros
    if (isInitialMount.current) {
      previousParamsRef.current = currentParams;
      isInitialMount.current = false;
      return;
    }

    // Verificar cambios usando Effect Event
    if (
      currentParams !== previousParamsRef.current &&
      checkParamChanges(currentParams)
    ) {
      setIsSearchLoading(true);

      // Limpiar timeout anterior
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }

      loadingTimeoutRef.current = setTimeout(() => {
        handleLoadingComplete();
      }, 300);

      previousParamsRef.current = currentParams;
    }
  }, [search]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  return {
    isSearchLoading,
    setSearchLoading: (loading: boolean) => {
      if (loading) {
        setIsSearchLoading(true);
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
        }
        loadingTimeoutRef.current = setTimeout(handleLoadingComplete, 300);
      } else {
        setIsSearchLoading(false);
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
        }
      }
    },
  };
}
